import BoxInfo from '@/components/boxInfo/boxInfo';
import DataGrid from '@/components/dataGrid/dataGrid';
import EmptyMessage from '@/components/emptyMessage';
import { useCurrentUser } from '@/modules/profile/hooks/useCurrentUser/useCurrentUser';
import ChipStatus from '@/modules/user/components/chipStatus/chipStatus';
import { UserResponse, UserSearchParams } from '@/modules/user/entities/user';
import {
  USER_PERMISSIONS,
  USER_STATUS,
  USER_STATUS_TRANSLATED,
  USER_TYPE,
} from '@/modules/user/entities/user.const';
import UserService from '@/modules/user/services/userService';
import React, { useMemo } from 'react';

interface TableAndFilterUserProps {
  userType: USER_TYPE;
}

type UserFilters = Partial<UserSearchParams>;

const USER_SORT_OPTIONS = [
  {
    value: 'name',
    text: 'Nome',
  },
  {
    value: '-createdAt',
    text: 'Criado em',
  },
  {
    value: '-lastAccessedAt',
    text: 'Último acesso em',
  },
];

const TableAndFilterUser: React.FC<TableAndFilterUserProps> = ({ userType }) => {
  const { checkUserHasPermission } = useCurrentUser();
  const hasPermission = useMemo(() => {
    const permission =
      userType === USER_TYPE.BROKER
        ? USER_PERMISSIONS.BROKER_USER_READ
        : USER_PERMISSIONS.COMPANY_USER_READ;

    return checkUserHasPermission(permission);
  }, [checkUserHasPermission, userType]);

  return hasPermission ? (
    <DataGrid<UserResponse, UserFilters>
      initialFilterValue={() => ({
        sort: 'name',
        status: USER_STATUS.ACTIVE,
      })}
      source={(filters, companyId) => {
        return new UserService().getUsers({
          ...filters,
          scopeId: userType === USER_TYPE.BROKER ? process.env.REACT_APP_BROKER_ID : companyId,
          type: userType,
          withDeleted: !filters.status || filters.status === USER_STATUS.INACTIVATED,
        });
      }}
      filters={[
        {
          label: 'Nome ou e-mail',
          key: 'q',
          width: 50,
        },
        {
          label: 'Status',
          type: 'select',
          data: Object.entries(USER_STATUS_TRANSLATED).map(([value, text]) => ({
            value,
            text,
          })),
          key: 'status',
          width: 25,
        },
        {
          label: 'Ordenação por',
          type: 'select',
          data: USER_SORT_OPTIONS,
          key: 'sort',
          width: 25,
        },
      ]}
      columns={[
        {
          headerName: 'Nome',
          field: 'name',
          sortable: true,
        },
        {
          headerName: 'E-mail',
          field: 'email',
          sortable: true,
        },
        {
          headerName: 'Status',
          field: 'status',
          render: ({ status }) => (status ? <ChipStatus status={status as USER_STATUS} /> : null),
          sortable: true,
        },
        {
          headerName: 'Último acesso em',
          field: 'lastAccessedAt',
          formatData: 'date',
          sortable: true,
        },
        {
          headerName: 'Criado em',
          field: 'createdAt',
          formatData: 'date',
          sortable: true,
        },
      ]}
      actionsByItem={[
        {
          redirectToDetail: ({ id, type, status }, companyId) => {
            const route =
              type === USER_TYPE.BROKER
                ? `/administrators/${id}/details`
                : `/companies/${companyId}/users/${id}/details`;

            if (status === USER_STATUS.INACTIVATED) {
              return `${route}?userStatus=${USER_STATUS.INACTIVATED}`;
            }

            return route;
          },
        },
      ]}
    />
  ) : (
    <BoxInfo>
      <EmptyMessage
        title='Sem permissão'
        description='Não é possível exibir os registros de usuários'
      />
    </BoxInfo>
  );
};

export default TableAndFilterUser;
